import React, { useCallback, useEffect, useState } from 'react';
import {
  useMigrateProjectMutation,
  UserProjectDocument,
} from 'components/Projects/operation.generated';
import { ProductUsageDocument } from 'components/ProductUsage/operation.generated';
import { SectionsDocument } from 'components/ScriptStudio/Sections/operation.generated';
import { useEffectiveStudioLimit } from 'components/ScriptStudio/Sections/hooks/useProjectSections';
import { toast } from 'components/Toaster';
import { EventName, useEventsContext } from 'context/events/EventsContext';
import LegacyProjectMigrationModal from './LegacyProjectMigrationModal';
import { StudioProjectVersion } from '../../../../../../server/graphql/__generated.types';

interface LegacyProjectMigrationModalContainerProps {
  open: boolean;
  projectId: string;
  projectName: string;
  studioVersion: StudioProjectVersion;
  helpArticleHref: string;
  onClose: () => void;
}

export default function LegacyProjectMigrationModalContainer({
  open,
  projectId,
  projectName,
  studioVersion,
  helpArticleHref,
  onClose,
}: LegacyProjectMigrationModalContainerProps) {
  const [eventTracking] = useEventsContext();
  const [isConverting, setIsConverting] = useState(false);
  const effectiveLimit = useEffectiveStudioLimit();
  const [migrateProject] = useMigrateProjectMutation({
    refetchQueries: [
      {
        query: UserProjectDocument,
        variables: { inputs: { projectId } },
      },
      {
        query: ProductUsageDocument,
      },
      // Migration creates section/take rows; script studio loads via this query after studioVersion flips.
      {
        query: SectionsDocument,
        variables: {
          inputs: { projectId, limit: effectiveLimit },
        },
      },
    ],
    awaitRefetchQueries: true,
  });

  useEffect(() => {
    if (!open) {
      setIsConverting(false);
    }
  }, [open]);

  const handleConvertNow = useCallback(async () => {
    setIsConverting(true);
    const { data } = await migrateProject({
      variables: { inputs: { projectId } },
    });
    const success = data?.migrateProject.success === true;

    eventTracking.track({
      type: EventName.ProjectMigrated,
      data: {
        projectId,
        studioVersion,
        success,
      },
    });

    if (!success) {
      setIsConverting(false);
      if (data?.migrateProject.message) {
        toast({
          message: data.migrateProject.message,
          type: 'error',
        });
      }
      return;
    }

    setIsConverting(false);
    onClose();
    toast({
      message: data?.migrateProject?.message || 'Project migrated successfully',
      type: 'success',
    });
  }, [studioVersion, eventTracking, migrateProject, onClose, projectId]);

  return (
    <LegacyProjectMigrationModal
      open={open}
      projectName={projectName}
      helpArticleHref={helpArticleHref}
      onClose={onClose}
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      onConvertNow={handleConvertNow}
      isConverting={isConverting}
    />
  );
}
